<template>
  <!-- ====== 简洁结果页（claritone-reader / fund-writing 通用，参考 result4.png 实际渲染截图）====== -->
  <!-- 【2026-06-18 改造】支持 fund-writing 工具：标题卡 + 复制/下载按钮 + 文档预览区 -->
  <div class="rr-page">
    <!-- 顶部 header — 浅灰色，左"运行结果" + 右"耗时 N 秒" -->
    <div class="rr-header">
      <span class="rr-header-title">运行结果</span>
      <span class="rr-header-time" v-if="elapsedMs">耗时 {{ formatDuration(elapsedMs) }}</span>
    </div>

    <!-- 【2026-06-18 新增】任务失败卡片 -->
    <div class="rr-error-card" v-if="errorMessage">
      <div class="rr-error-icon">&#9888;</div>
      <div class="rr-error-body">
        <div class="rr-error-title">提交失败</div>
        <div class="rr-error-detail">{{ errorMessage }}</div>
      </div>
    </div>

    <!-- 【2026-06-18 新增】标题卡（白底+细边框，左侧标题+元信息，右侧操作按钮） -->
    <!--   用于 fund-writing：显示工具元信息（基金申请书正文稿 / 中文 · 宋体 Word 导出） + 复制/下载按钮 -->
    <!--   复用规则：showTitleCard 为 true 时才显示（默认 false，claritone-reader 不显示） -->
    <div class="rr-title-card" v-if="showTitleCard && !errorMessage">
      <div class="rr-title-card-info">
        <div class="rr-title-card-title">{{ cardTitle || '运行结果' }}</div>
        <div class="rr-title-card-meta" v-if="metaLine">{{ metaLine }}</div>
      </div>
      <div class="rr-title-card-actions">
        <!-- 复制正文按钮（白底+深绿描边，深绿文字） -->
        <button
          class="rr-action-btn"
          :class="{ 'rr-action-btn-success': copyFeedback === 'success', 'rr-action-btn-error': copyFeedback === 'error' }"
          v-if="copyText"
          @click="handleCopyText"
        >
          <span class="rr-action-icon">{{ copyFeedback === 'success' ? '✓' : copyFeedback === 'error' ? '✕' : '📋' }}</span>
          <span>{{ copyFeedback === 'success' ? '已复制' : copyFeedback === 'error' ? '复制失败' : '复制正文' }}</span>
        </button>
        <!-- 下载 Word 按钮（白底+深绿描边，深绿文字） -->
        <button
          class="rr-action-btn"
          :class="{ 'rr-action-btn-success': downloadFeedback === 'success', 'rr-action-btn-error': downloadFeedback === 'error' }"
          v-if="docxBase64"
          @click="handleDownloadDocx"
        >
          <span class="rr-action-icon">{{ downloadFeedback === 'success' ? '✓' : downloadFeedback === 'error' ? '✕' : '⬇' }}</span>
          <span>{{ downloadFeedback === 'success' ? '已下载' : downloadFeedback === 'error' ? '下载失败' : '下载 Word' }}</span>
        </button>
      </div>
    </div>

    <!-- 文档预览区（白底+细边框）— 直接渲染 markdown -->
    <!-- 【2026-06-18 改造】rr-body 改为白底，模拟文档纸张效果 -->
    <div class="rr-body" :class="{ 'rr-body-has-card': showTitleCard }" v-if="!errorMessage">
      <div class="rr-content" v-html="renderedContent"></div>
    </div>
  </div>
</template>

<script>
// 【重要】renderMarkdown 函数从 ResultPage.vue 复制（19 步处理逻辑）
//  根因：claritone-reader (paper_qa) 响应只有 markdown 字段，渲染需求与 claritone-pubmed 完全不同
//        需要一个简洁的页面（无统计卡/数据源 chip/文献表/综合分析/折叠面板）
//  保持与 ResultPage.vue 完全相同的列表 div 化、DOI 链接、引用徽章、GFM 表格等能力
//  避免重构 utils.js 带来的回归风险（ResultPage.vue 深度依赖此函数）
export default {
  name: 'ReaderResultPage',
  props: {
    title: { type: String, default: '' },
    elapsedMs: { type: Number, default: 0 },
    markdown: { type: String, default: '' },
    // 【2026-06-18 新增】fund-writing 标题卡相关 props
    //  标题卡显示控制：true = 显示（fund-writing），false = 不显示（claritone-reader）
    showTitleCard: { type: Boolean, default: false },
    // 标题卡大标题（如"基金申请书正文稿"），不传则用 title
    cardTitle: { type: String, default: '' },
    // 标题卡副标题/元信息行（如"中文 · 宋体 Word 导出"）
    metaLine: { type: String, default: '' },
    // 复制正文按钮用
    copyText: { type: String, default: '' },
    // 【2026-06-18 新增】任务失败时显示的错误详情
    errorMessage: { type: String, default: '' },
    // 下载 Word 按钮用
    docxBase64: { type: String, default: '' },
    docxFilename: { type: String, default: 'document.docx' }
  },
  data() {
    return {
      // 【2026-06-18】复制反馈状态：'idle' | 'success' | 'error' — 以按钮内文切换代替 $message（项目不依赖 Element UI）
      copyFeedback: 'idle',
      downloadFeedback: 'idle'
    }
  },
  computed: {
    renderedContent() {
      return this.renderMarkdown(this.markdown || '')
    }
  },
  methods: {
    formatDuration(ms) {
      if (!ms) return '0秒'
      const sec = Math.floor(ms / 1000)
      if (sec < 60) return sec + '秒'
      const min = Math.floor(sec / 60)
      const remSec = sec % 60
      return min + '分' + (remSec > 0 ? remSec + '秒' : '')
    },
    // 【2026-06-18 重写】复制正文到剪贴板
    //  根因：上一版三个问题叠加导致「点击没反应」：
    //   1) 项目不依赖 Element UI，`this.$message` 是 undefined，`?.success` 静默吞错；
    //   2) `navigator.clipboard.writeText` 在非 secure context（http://localhost/未启用 https）会被拒绝
    //      且抛 SecurityError，走 catch 后也无法呈现反馈；
    //   3) fallback `document.execCommand('copy')` 同样可能被 permissions policy 阻断，未读返回值。
    //  修复：
    //   - execCommand 路径作为首选（兼容性最好）；clipboard API 作为增强
    //   - 反馈走「按钮内文 + 状态类名」不依赖 $message
    //   - 2 秒后自动复位按钮文案
    async handleCopyText() {
      const text = this.copyText || this.markdown || ''
      if (!text) {
        this._setCopyFeedback('error', '没有可复制的内容')
        return
      }

      let success = false

      // 主路径：execCommand('copy') — 在 http://localhost 下也能用
      try {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.setAttribute('readonly', '')
        ta.style.position = 'fixed'
        ta.style.top = '0'
        ta.style.left = '-9999px'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.focus()
        ta.select()
        ta.setSelectionRange(0, text.length)
        success = document.execCommand('copy')
        document.body.removeChild(ta)
      } catch (e) {
        console.warn('[ReaderResultPage] execCommand 路径失败:', e)
      }

      // 增强路径：clipboard API（仅 secure context）
      if (!success && navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text)
          success = true
        } catch (e) {
          console.warn('[ReaderResultPage] clipboard API 路径失败:', e)
        }
      }

      this._setCopyFeedback(success ? 'success' : 'error', success ? '已复制' : '复制失败')
    },

    // 【2026-06-18 新增】复制反馈状态控制（按钮内文切换 + 2s 后复位）
    _setCopyFeedback(type, msg) {
      this.copyFeedback = type
      console.log('[ReaderResultPage]', msg)
      clearTimeout(this._copyFbTimer)
      this._copyFbTimer = setTimeout(() => {
        this.copyFeedback = 'idle'
      }, 2000)
    },
    // 【2026-06-18 重写】下载 Word（base64 → Blob → a 标签触发下载）
    //  根因同复制：原版 $message 静默吞错。改为按钮内文反馈
    handleDownloadDocx() {
      const b64 = this.docxBase64
      if (!b64) {
        this._setDownloadFeedback('error', '没有可下载的文件')
        return
      }
      try {
        // base64 → Uint8Array（兼容含 data: 前缀的情况）
        const cleaned = b64.includes(',') ? b64.split(',')[1] : b64
        const binary = atob(cleaned)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i)
        }
        const blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = this.docxFilename || 'document.docx'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        // 延迟释放 URL，让浏览器有足够时间处理下载
        setTimeout(() => URL.revokeObjectURL(url), 1000)
        this._setDownloadFeedback('success', 'Word 文档已开始下载')
      } catch (e) {
        console.error('[ReaderResultPage] 下载 Word 失败:', e)
        this._setDownloadFeedback('error', '下载失败：' + (e.message || '未知错误'))
      }
    },
    // 【2026-06-18 新增】下载反馈状态控制（按钮内文 + 状态类名 + 2s 后复位）
    _setDownloadFeedback(type, msg) {
      this.downloadFeedback = type
      console.log('[ReaderResultPage]', msg)
      clearTimeout(this._downloadFbTimer)
      this._downloadFbTimer = setTimeout(() => {
        this.downloadFeedback = 'idle'
      }, 2000)
    },
    renderMarkdown(md) {
      if (!md || typeof md !== 'string') return ''
      let html = md
      // 0-pre. 【2026-06-18】预处理：行首 "数字) " 转为 "数字. "，让 17 步能识别为数字列表
      //   根因：paper_qa (claritone-reader) 响应习惯用 "1) Direct answer" 格式（带括号）
      //   原 renderMarkdown 17 步只识别 "1. xxx"（句点），"1)" 被当成普通段落
      //   老板截图："1) Direct answer / 2) Evidence snippets / 3) Confidence" 都未被识别
      //   用 ^ + m flag 限定行首，避免误伤正文里的 "1) xxx" 引用
      html = html.replace(/^(\d+)\)\s+/gm, '$1. ')
      // 0a. 删除「筛选条件」整段
      html = html.replace(/^[ \t]*筛选条件[ \t]*$\n((?:[-*]\s+[^\n]*\n?)+?)(?=\n[ \t]*\n|\n#{1,6}[ \t]+)/gm, '')
      html = html.replace(/^[ \t]*筛选条件[ \t]*$\n((?:[-*]\s+[^\n]*\n?)+)$/gm, '')
      // 0b. 删除「检索关键词」整段
      html = html.replace(/^#{1,6}[ \t]*检索关键词[ \t]*$\n(?:[^\n]+\n)+?(?=\n[ \t]*\n|\n#{1,6}[ \t]+)/gm, '')
      html = html.replace(/^#{1,6}[ \t]*检索关键词[ \t]*$\n(?:[^\n]+\n)+$/gm, '')
      // 1. 转义 HTML（先做）
      html = html.replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      // 2. 代码块
      html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (m, lang, code) => '<pre class="md-code"><code>' + code + '</code></pre>')
      // 3. 标题
      html = html.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>')
        .replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>')
        .replace(/^####\s+(.+)$/gm, '<h4>$1</h4>')
        .replace(/^###\s+(.+)$/gm, '<h3>$1</h3>')
        .replace(/^##\s+(.+)$/gm, '<h2>$1</h2>')
        .replace(/^#\s+(.+)$/gm, '<h1>$1</h1>')
      // 4. GFM 表格
      html = html.replace(/((?:^\|.+\|\s*\n)+)(^\|[\s\-:|]+\|\s*\n)((?:^\|.+\|\s*\n?)+)/gm, (m, headerBlock, sepBlock, dataBlock) => {
        const parseRow = (line) => {
          return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim())
        }
        const firstHeaderLine = headerBlock.split('\n').find(l => l.trim().startsWith('|'))
        if (!firstHeaderLine) return m
        const headers = parseRow(firstHeaderLine)
        const dataRows = dataBlock.trim().split('\n').filter(line => line.trim().startsWith('|')).map(parseRow)
        let t = '<div class="rp-md-table-wrap"><table class="rp-md-table"><thead><tr>'
        headers.forEach(h => { t += '<th>' + h + '</th>' })
        t += '</tr></thead><tbody>'
        dataRows.forEach(row => {
          t += '<tr>'
          headers.forEach((_, i) => {
            t += '<td>' + (row[i] !== undefined ? row[i] : '') + '</td>'
          })
          t += '</tr>'
        })
        t += '</tbody></table></div>'
        return t + '\n'
      })
      // 5. 多行伪表格
      html = html.replace(/(?:^\|[^\n]+\|\s*\n?){2,}/gm, (block) => {
        const lines = block.trim().split('\n').filter(l => l.trim().startsWith('|') && l.trim().endsWith('|'))
        if (lines.length < 2) return block
        const out = lines.map(line => {
          const cells = line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim()).filter(c => c !== '')
          if (cells.length === 0) return ''
          if (cells.every(c => /^\d+\.\s/.test(c))) {
            return '<div class="rp-md-list rp-md-list-num">' + cells.map(c => '<div class="rp-md-li">' + c.replace(/^\d+\.\s+/, '') + '</div>').join('') + '</div>'
          }
          return cells.map(c => '<p class="rp-md-pipe-line">' + c + '</p>').join('')
        }).join('\n')
        return out + '\n'
      })
      // 6. 单行伪表格
      html = html.replace(/^\|([^\n]+)\|$/gm, (m, content) => {
        const cells = content.split('|').map(c => c.trim()).filter(c => c !== '')
        if (cells.length < 2) return m
        if (cells.every(c => /^\d+\.\s/.test(c))) {
          return '<div class="rp-md-list rp-md-list-num">' + cells.map(c => '<div class="rp-md-li">' + c.replace(/^\d+\.\s+/, '') + '</div>').join('') + '</div>'
        }
        return cells.map(c => '<p class="rp-md-pipe-line">' + c + '</p>').join('\n')
      })
      // 7. 引用块
      html = html.replace(/^>\s+(.+)$/gm, '<blockquote>$1</blockquote>')
      // 8. 粗体 / 斜体
      html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      html = html.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
      // 9. 行内代码
      html = html.replace(/`([^`\n]+)`/g, '<code class="md-inline-code">$1</code>')
      // 10. 引用信息徽章
      html = html.replace(/[（(]\s*(\d{4})\s*年?,?\s*[,，]?\s*([^,，()]+?)\s*[,，]\s*引用?\s*(\d+)\s*次?\s*[)）]/g,
        '<span class="rp-citation">$1 · $2 · 引用 $3</span>')
      // 11+12. DOI 链接（占位符机制）
      const doiHolder = []
      html = html.replace(/(DOI[:：]\s*)?(10\.\d{4,}\/[^\s<&"<>]+)/gi, (m, prefix, doi) => {
        const idx = doiHolder.length
        doiHolder.push({ prefix: prefix || '', doi: doi })
        return '\x00DOI' + idx + '\x00'
      })
      // 13. 链接
      html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
      // 14. 裸 URL
      html = html.replace(/(?<!["=>])(https?:\/\/[^\s<"]+)(?!<\/a>|"<)/g,
        '<a href="$1" target="_blank" rel="noopener">$1</a>')
      // 15. 水平线
      html = html.replace(/^---+$/gm, '<hr/>')
      // 15.5. 单行内多列表项折行
      html = html.split('\n').map(line => {
        const re = /(?<=^|[\s\u4e00-\u9fff，。：；、！？（）]|[.,;:!?\(\)\[\]"'`])(\*)(?=\s)/g
        const matches = []
        let mm
        while ((mm = re.exec(line)) !== null) matches.push(mm.index)
        if (matches.length < 2) return line
        const indent = (line.match(/^[ \t]*/) || [''])[0]
        let result = line.substring(0, matches[0] + 2)
        let lastEnd = matches[0] + 2
        for (let i = 1; i < matches.length; i++) {
          const idx = matches[i]
          result += line.substring(lastEnd, idx - 1) + '\n' + indent + line.substring(idx, idx + 2)
          lastEnd = idx + 2
        }
        result += line.substring(lastEnd)
        return result
      }).join('\n')
      // 16. 无序列表（嵌套 div 化）
      html = html.replace(/(^|\n)((?:[ \t]*[-*]\s+.+\n?)+)/g, (m, prefix, block) => {
        const rawLines = block.split('\n')
        const lines = []
        for (const l of rawLines) {
          if (/^\s*\d+\.\s/.test(l)) break
          if (l.trim()) lines.push(l)
        }
        if (lines.length === 0) return m
        const items = lines.map(l => {
          const im = l.match(/^([ \t]*)/)[1]
          const iw = im.replace(/\t/g, '    ').length
          const c = l.replace(/^[ \t]*[-*]\s+/, '').trim()
          return { indent: iw, content: c }
        })
        const minIndent = Math.min(...items.map(i => i.indent))
        const render = (arr, base) => {
          const out = []
          let i = 0
          while (i < arr.length) {
            const it = arr[i]
            if (it.indent < base) break
            if (it.indent === base) {
              const sub = []
              let j = i + 1
              while (j < arr.length && arr[j].indent > base) {
                sub.push(arr[j])
                j++
              }
              if (sub.length > 0) {
                const subMin = Math.min(...sub.map(s => s.indent))
                out.push('<div class="rp-md-li">' + it.content + '\n' + render(sub, subMin) + '\n</div>')
                i = j
              } else {
                out.push('<div class="rp-md-li">' + it.content + '</div>')
                i++
              }
            } else {
              i++
            }
          }
          return '<div class="rp-md-list">\n' + out.join('\n') + '\n</div>'
        }
        return prefix + render(items, minIndent) + '\n'
      })
      // 16.5 合并"数字列表单项 + 紧跟的 <div class='rp-md-list'> 块"
      {
        const lines = html.split('\n')
        const out = []
        let i = 0
        while (i < lines.length) {
          const line = lines[i]
          if (/^\d+\.\s+/.test(line) && i + 1 < lines.length && lines[i + 1].startsWith('<div class="rp-md-list"')) {
            let depth = 0
            let endLine = i + 1
            for (let j = i + 1; j < lines.length; j++) {
              const l = lines[j]
              const opens = (l.match(/<div\b/g) || []).length
              const closes = (l.match(/<\/div>/g) || []).length
              depth += opens - closes
              if (depth === 0) { endLine = j; break }
            }
            const headerText = line.replace(/^\d+\.\s+/, '').trim()
            const innerList = lines.slice(i + 1, endLine + 1).join('\n')
            out.push('<div class="rp-md-list rp-md-list-num"><div class="rp-md-li">' + headerText + innerList + '</div></div>')
            i = endLine + 1
          } else {
            out.push(line)
            i++
          }
        }
        html = out.join('\n')
      }
      // 17. 数字列表（至少 2 项）
      html = html.replace(/(^|\n)((?:[ \t]*\d+\.\s+.+\n?){2,})/g, (m, prefix, list) => {
        const items = list.split('\n').filter(line => line.trim()).map(line => '<div class="rp-md-li">' + line.replace(/^[ \t]*\d+\.\s+/, '') + '</div>').join('')
        return prefix + '<div class="rp-md-list rp-md-list-num">' + items + '</div>'
      })
      // 18. 段落
      html = html.split(/\n{2,}/).map(block => {
        const trimmed = block.trim()
        if (!trimmed) return ''
        if (/^<(h[1-6]|ul|ol|pre|blockquote|hr|p|table|div)/.test(trimmed)) return block
        if (/<div class="rp-md-(list|li)"/.test(trimmed)) return block
        return '<p>' + trimmed.replace(/\n/g, '<br/>') + '</p>'
      }).join('\n')
      // 19. 还原 DOI 占位符
      doiHolder.forEach((d, i) => {
        const token = '\x00DOI' + i + '\x00'
        html = html.split(token).join('<a class="rp-doi-link" href="https://doi.org/' + d.doi + '" target="_blank" rel="noopener">' + d.prefix + d.doi + '</a>')
      })
      return html
    }
  }
}
</script>

<style scoped>
/* ====== 整体容器 — 浅灰色，圆角 ====== */
.rr-page {
  margin-top: 20px;
  background: #f0f0f0;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
}

/* 顶部 header — 稍深的浅灰，左标题右耗时 */
.rr-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  background: #e8e8e8;
  border-bottom: 1px solid #d8d8d8;
}
.rr-header-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
}
.rr-header-time {
  font-size: 12.5px;
  color: #777;
}

/* ====== 标题卡（fund-writing 专用）====== */
.rr-title-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 14px 14px 0 14px;
  padding: 18px 22px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}
.rr-title-card-info {
  flex: 1;
  min-width: 0;
  padding-right: 16px;
}
.rr-title-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.4;
  margin-bottom: 4px;
}
.rr-title-card-meta {
  font-size: 12px;
  color: #888;
  letter-spacing: 0.2px;
}
.rr-title-card-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.rr-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: #ffffff;
  border: 1px solid #c8d4cc;
  border-radius: 6px;
  color: #2d6a4f;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;
}
.rr-action-btn:hover {
  background: #f0f5f2;
  border-color: #2d6a4f;
  color: #1b4332;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(45, 106, 79, 0.12);
}
.rr-action-btn:active {
  transform: translateY(0);
  box-shadow: none;
}
/* 【2026-06-18 新增】复制/下载反馈状态色 — 不依赖 $message，用状态色变化呈现成功/失败 */
.rr-action-btn-success {
  background: #e6f4ea;
  border-color: #2d6a4f;
  color: #1b4332;
}
.rr-action-btn-success:hover {
  background: #d8ebe0;
  border-color: #1b4332;
  color: #1b4332;
}
.rr-action-btn-error {
  background: #fdecea;
  border-color: #c0392b;
  color: #a93226;
}
.rr-action-btn-error:hover {
  background: #f9d8d4;
  border-color: #a93226;
  color: #a93226;
}
.rr-action-icon {
  font-size: 14px;
  line-height: 1;
}

/* ====== 文档预览区（白底+细边框，模拟文档纸张）====== */
.rr-body {
  margin: 14px;
  padding: 28px 36px 32px 36px;
  background: #ffffff;
  color: #1a1a1a;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  max-height: 500px;
  overflow-y: auto;
}
/* 没有标题卡时 rr-body 上边距收一点 */
.rr-body:not(.rr-body-has-card) {
  margin: 14px;
}
/* 【2026-06-18 新增】任务失败卡片 */
.rr-error-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fff5f5;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 16px 20px;
  margin: 16px 24px 16px;
}
.rr-error-icon {
  font-size: 20px;
  line-height: 1.4;
  flex-shrink: 0;
  color: #dc2626;
}
.rr-error-body {
  flex: 1;
  min-width: 0;
}
.rr-error-title {
  font-size: 14px;
  font-weight: 600;
  color: #b91c1c;
  margin-bottom: 4px;
}
.rr-error-detail {
  font-size: 12px;
  color: #991b1b;
  line-height: 1.5;
  word-break: break-word;
}


/* 内容区 — 复用 rp-md-* 类（与 ResultPage.vue 保持一致）*/
.rr-content {
  font-size: 14px;
  line-height: 1.7;
  color: #1a1a1a;
}

/* 标题层级 */
.rr-content :deep(h1) {
  font-size: 14px;
  font-weight: 600;
  margin: 18px 0 12px 0;
}
.rr-content :deep(h2) {
  font-size: 14px;
  font-weight: 600;
  margin: 16px 0 10px 0;
}
.rr-content :deep(h3) {
  font-size: 14px;
  font-weight: 600;
  margin: 14px 0 8px 0;
}
.rr-content :deep(h4) {
  font-size: 14.5px;
  font-weight: 600;
  margin: 12px 0 6px 0;
}
.rr-content :deep(h5),
.rr-content :deep(h6) {
  font-size: 14px;
  font-weight: 600;
  margin: 10px 0 6px 0;
}

/* 段落 */
.rr-content :deep(p) {
  margin: 8px 0;
  line-height: 1.7;
}

/* 粗体 */
.rr-content :deep(strong) {
  font-weight: 600;
  color: #1a1a1a;
}

/* 行内代码 */
.rr-content :deep(.md-inline-code) {
  padding: 1px 6px;
  background: #e8e8e8;
  border-radius: 3px;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
  color: #2a2a2a;
}

/* 代码块 */
.rr-content :deep(.md-code) {
  background: #2a2a2a;
  color: #f0f0f0;
  padding: 12px 16px;
  border-radius: 6px;
  overflow-x: auto;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 12.5px;
  line-height: 1.6;
  margin: 12px 0;
}
.rr-content :deep(.md-code code) {
  background: transparent;
  padding: 0;
}

/* 引用块 */
.rr-content :deep(blockquote) {
  border-left: 3px solid #c8c8c8;
  padding: 4px 0 4px 14px;
  margin: 10px 0;
  color: #555;
  background: transparent;
  font-style: italic;
}

/* 链接 */
.rr-content :deep(a) {
  color: #1a3a2e;
  text-decoration: underline;
}
.rr-content :deep(a:hover) {
  color: #2d6a4f;
}

/* DOI 链接 */
.rr-content :deep(.rp-doi-link) {
  color: #1a3a2e;
  text-decoration: underline;
  font-weight: 500;
}

/* 引用信息徽章 */
.rr-content :deep(.rp-citation) {
  display: inline-block;
  padding: 1px 8px;
  background: #e8e8e8;
  color: #1a3a2e;
  border-radius: 10px;
  font-size: 12px;
  margin: 0 2px;
}

/* ===== 列表 div 化（继承 ResultPage 的 .rp-md-list 命名）===== */
.rr-content :deep(.rp-md-list) {
  margin: 10px 0;
  padding-left: 28px;
  list-style: none;
}
.rr-content :deep(.rp-md-li) {
  margin: 6px 0;
  line-height: 1.7;
  position: relative;
}
.rr-content :deep(.rp-md-li::before) {
  content: '•';
  position: absolute;
  left: -18px;
  color: #1a3a2e;
  font-weight: 700;
}

/* 数字列表（1. 2. 3.）— 用 counter 重置+累加 */
.rr-content :deep(.rp-md-list-num) {
  padding-left: 32px;
  counter-reset: rr-num 0;
}
.rr-content :deep(.rp-md-list-num > .rp-md-li) {
  counter-increment: rr-num 1;
}
.rr-content :deep(.rp-md-list-num > .rp-md-li::before) {
  content: counter(rr-num) '.';
  position: absolute;
  left: -28px;
  color: #1a1a1a;
  font-weight: 600;
  background: transparent;
  border-radius: 0;
  padding: 0;
  font-size: 14px;
}

/* 水平线 */
.rr-content :deep(hr) {
  border: none;
  border-top: 1px solid #d8d8d8;
  margin: 16px 0;
}

/* GFM 表格 — 简洁风格 */
.rr-content :deep(.rp-md-table-wrap) {
  overflow-x: auto;
  margin: 12px 0;
  border: 1px solid #d8d8d8;
  border-radius: 6px;
  background: #fafafa;
}
.rr-content :deep(.rp-md-table) {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.rr-content :deep(.rp-md-table th) {
  padding: 8px 12px;
  background: #e8e8e8;
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid #d8d8d8;
}
.rr-content :deep(.rp-md-table td) {
  padding: 8px 12px;
  border-bottom: 1px solid #ececec;
  color: #2a2a2a;
}
.rr-content :deep(.rp-md-table tr:last-child td) {
  border-bottom: none;
}

/* 响应式 */
@media (max-width: 768px) {
  .rr-body {
    padding: 18px 18px 20px 18px;
  }
  .rr-title {
    font-size: 18px;
  }
  .rr-content :deep(h1) { font-size: 18px; }
  .rr-content :deep(h2) { font-size: 16px; }
  .rr-content :deep(h3) { font-size: 15px; }
}
</style>
